<template>
  <div class="sgc-app">

    <!-- ══ Bandeau d'état ══ -->
    <div :class="['sgc-bar', 'sgc-bar-' + etat]">
      <i :class="iconeEtat"></i>
      <div class="sgc-bar-txt">
        <strong>{{ titreEtat }}</strong>
        <small>{{ sousTitreEtat }}</small>
      </div>
      <button v-if="torcheDispo" :class="['sgc-torch', { on: torcheOn }]" @click="basculerTorche">
        <i :class="torcheOn ? 'ri-flashlight-fill' : 'ri-flashlight-line'"></i>
      </button>
    </div>

    <!-- ══ Caméra ══ -->
    <div class="sgc-stage">
      <video ref="video" class="sgc-video" playsinline muted></video>
      <div class="sgc-frame"><span></span><span></span><span></span><span></span></div>

      <!-- Accusé d'envoi -->
      <transition name="sgc-pop">
        <div v-if="accuse" class="sgc-accuse">
          <i class="ri-check-line"></i>
          <strong>Envoyé à la caisse</strong>
          <small>{{ accuse }}</small>
        </div>
      </transition>

      <!-- Erreurs caméra / lien -->
      <div v-if="etat === 'refus'" class="sgc-cover">
        <i class="ri-link-unlink"></i>
        <p>Lien invalide ou expiré</p>
        <small>Demandez à la caisse d'afficher un nouveau QR d'appairage, puis rescannez-le.</small>
      </div>
      <div v-else-if="cameraKo" class="sgc-cover">
        <i class="ri-camera-off-line"></i>
        <p>Caméra indisponible</p>
        <small>
          Autorisez l'accès à la caméra dans le navigateur.
          Une connexion sécurisée (HTTPS) est nécessaire.
        </small>
      </div>
    </div>

    <!-- ══ Pied ══ -->
    <div class="sgc-foot">
      <div class="sgc-compteur">
        <i class="ri-send-plane-2-line"></i>
        <span>{{ nbenvois }} envoi(s)</span>
      </div>
      <p class="sgc-aide">
        Visez le QR du téléphone client, ou le code-barres d'un article.
      </p>
    </div>

  </div>
</template>

<script>
import { QrScanner } from "@/service/qrscanner";
import { ScangoSocket } from "@/service/scangosocket";
import { parsePayloadScanGo } from "@/service/scangopanier";

/* Le même code reste dans le champ de la caméra plusieurs secondes : sans ce
   verrou, un seul article serait envoyé des dizaines de fois. */
const ANTI_DOUBLON_MS = 1500;

const DUREE_ACCUSE_MS = 1200;

export default {
  /* eslint-disable */
  name: "scancaisseapp",

  data() {
    return {
      room: "",
      token: "",
      etat: "connexion",      // connexion | pret | coupure | refus
      cameraKo: false,
      torcheOn: false,
      torcheDispo: false,
      accuse: "",
      nbenvois: 0,
      dernierCode: "",
      dernierAt: 0,
    };
  },

  computed: {
    iconeEtat() {
      if (this.etat === "pret") return "ri-wifi-line";
      if (this.etat === "refus") return "ri-link-unlink";
      if (this.etat === "coupure") return "ri-wifi-off-line";
      return "ri-loader-4-line sgc-spin";
    },
    titreEtat() {
      if (this.etat === "pret") return "Connecté à la caisse";
      if (this.etat === "refus") return "Lien expiré";
      if (this.etat === "coupure") return "Caisse injoignable";
      return "Connexion…";
    },
    sousTitreEtat() {
      if (this.etat === "pret") return "Prêt à scanner";
      if (this.etat === "refus") return "Rescannez le QR d'appairage";
      if (this.etat === "coupure") return "Nouvelle tentative en cours…";
      return "Liaison avec la caisse";
    },
  },

  created() {
    /* Hors de data() : ni le socket ni le MediaStream n'ont à être proxifiés par Vue. */
    this.scanner = new QrScanner();
    this.sock = new ScangoSocket();
  },

  methods: {
    connecter() {
      this.sock.connecter(
          { room: this.room, token: this.token, role: "phone" },
          {
            onConnect: () => { this.etat = "pret"; },
            /* Un refus PAIR_* est définitif : inutile de laisser croire à une
               reconnexion possible, il faut un nouvel appairage. */
            onRefus: () => { this.etat = "refus"; this.scanner.arreter(); },
            onCoupure: () => { if (this.etat !== "refus") this.etat = "coupure"; },
          }
      );
    },

    async demarrerCamera() {
      const ok = await this.scanner.demarrer(this.$refs.video, {
        facingMode: "environment",
        /* QR panier ET codes-barres articles : le téléphone sert aussi de
           douchette sans fil pour la caisse. */
        formats: ["qr_code", "ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf"],
        onDecoded: (txt) => this.onDecoded(txt),
      });
      this.cameraKo = !ok;
      this.torcheDispo = this.scanner.torcheDisponible();
    },

    onDecoded(txt) {
      const code = String(txt || "").trim();
      if (code === "") return;
      if (this.etat !== "pret") return;

      const now = Date.now();
      if (code === this.dernierCode && now - this.dernierAt < ANTI_DOUBLON_MS) return;
      this.dernierCode = code;
      this.dernierAt = now;

      /* Le téléphone ne juge pas : il relaie. La caisse décide si c'est un panier
         ou un article, et le caissier contrôle. On distingue seulement les deux
         cas pour l'accusé affiché ici. */
      const panier = parsePayloadScanGo(code);
      const parti = this.sock.emettre("panier", { txt: code });

      /* Ne JAMAIS accuser réception de ce qui n'est pas parti : le caissier
         passerait à l'article suivant en le croyant enregistré, et il manquerait
         au ticket sans que personne ne s'en aperçoive. */
      if (!parti) {
        this.etat = "coupure";
        this.dernierCode = "";   // rescan immédiat autorisé, sans attendre l'anti-doublon
        return;
      }

      this.nbenvois++;
      this.accuse = panier
          ? "Panier de " + panier.l.length + " article(s)"
          : "Code " + code;
      this.feedback();
      setTimeout(() => { this.accuse = ""; }, DUREE_ACCUSE_MS);
    },

    feedback() {
      try { if (navigator.vibrate) navigator.vibrate(60); } catch (e) { /* ignore */ }
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 880;
        gain.gain.value = 0.1;
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start();
        setTimeout(() => { osc.stop(); ctx.close(); }, 90);
      } catch (e) { /* ignore */ }
    },

    async basculerTorche() {
      this.torcheOn = await this.scanner.basculerTorche();
    },
  },

  async mounted() {
    const p = this.$route.params || {};
    this.room = p.room || "";
    this.token = p.token || "";

    if (this.room === "" || this.token === "") {
      this.etat = "refus";
      return;
    }

    this.connecter();
    await this.demarrerCamera();
  },

  beforeUnmount() {
    this.scanner.arreter();
    this.sock.deconnecter();
  },
};
</script>

<style scoped>
.sgc-app {
  position: fixed; inset: 0;
  background: #06231b; color: #fff;
  display: flex; flex-direction: column;
  font-family: 'Segoe UI', system-ui, sans-serif;
  overflow: hidden;
}

/* ── Bandeau ── */
.sgc-bar {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; flex-shrink: 0;
  background: #0f6e56; transition: background .2s;
}
.sgc-bar > i { font-size: 20px; flex-shrink: 0; }
.sgc-bar-txt { display: flex; flex-direction: column; line-height: 1.25; flex: 1; min-width: 0; }
.sgc-bar-txt strong { font-size: 14px; font-weight: 800; }
.sgc-bar-txt small { font-size: 11px; opacity: .8; }
.sgc-bar-connexion { background: #475569; }
.sgc-bar-pret      { background: #0f6e56; }
.sgc-bar-coupure   { background: #b45309; }
.sgc-bar-refus     { background: #b91c1c; }
.sgc-spin { display: inline-block; animation: sgc-spin .8s linear infinite; }
@keyframes sgc-spin { to { transform: rotate(360deg); } }

.sgc-torch {
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
  border: 1px solid rgba(255,255,255,.35); background: rgba(255,255,255,.12);
  color: #fff; font-size: 17px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.sgc-torch.on { background: #fed577; color: #775a01; border-color: #fed577; }

/* ── Caméra ── */
.sgc-stage { position: relative; flex: 1; min-height: 0; background: #000; overflow: hidden; }
.sgc-video { width: 100%; height: 100%; object-fit: cover; display: block; }

.sgc-frame { position: absolute; inset: 18% 12%; pointer-events: none; }
.sgc-frame span { position: absolute; width: 30px; height: 30px; border: 3px solid #fed577; }
.sgc-frame span:nth-child(1) { top: 0; left: 0; border-right: none; border-bottom: none; border-radius: 10px 0 0 0; }
.sgc-frame span:nth-child(2) { top: 0; right: 0; border-left: none; border-bottom: none; border-radius: 0 10px 0 0; }
.sgc-frame span:nth-child(3) { bottom: 0; left: 0; border-right: none; border-top: none; border-radius: 0 0 0 10px; }
.sgc-frame span:nth-child(4) { bottom: 0; right: 0; border-left: none; border-top: none; border-radius: 0 0 10px 0; }

.sgc-accuse {
  position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  background: rgba(15,110,86,.95); border-radius: 16px; padding: 20px 26px;
  text-align: center; box-shadow: 0 12px 40px rgba(0,0,0,.5);
}
.sgc-accuse i { font-size: 40px; }
.sgc-accuse strong { font-size: 15px; font-weight: 800; }
.sgc-accuse small { font-size: 11px; opacity: .85; }
.sgc-pop-enter-active, .sgc-pop-leave-active { transition: all .18s cubic-bezier(.4,0,.2,1); }
.sgc-pop-enter-from, .sgc-pop-leave-to { opacity: 0; transform: translate(-50%,-50%) scale(.8); }

.sgc-cover {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; text-align: center; padding: 28px;
  background: rgba(0,0,0,.85);
}
.sgc-cover i { font-size: 44px; opacity: .9; }
.sgc-cover p { font-size: 16px; font-weight: 800; margin: 0; }
.sgc-cover small { font-size: 12px; opacity: .7; line-height: 1.5; max-width: 280px; }

/* ── Pied ── */
.sgc-foot {
  flex-shrink: 0; padding: 12px 14px 18px;
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  background: #06231b;
}
.sgc-compteur {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 700; color: #fed577;
}
.sgc-aide { font-size: 11px; opacity: .6; margin: 0; text-align: center; line-height: 1.4; }
</style>
